import {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, tailwindOverride } from '@getsynapse/design-system';
import { Request } from 'types/store/normalized';
import SkeletonLoader from 'Organisms/TableSkeletonLoader/PageTableLoader';
import { setSelectAll } from 'state/ExportRequests/ExportRequestsSlice';
import {
  requestsSelectors,
  clearLastPatchedRequestId,
} from 'state/normalized/requests/requestsSlice';
import RequestsTableBody from './RequestsTableBody/RequestsTableBody';
import RequestsTableHeader from './RequestsTableHeader/RequestsTableHeader';
import { SLICE_STATUS } from 'utils/constants';

interface RequestsTableProps {
  requests: Request[];
  status: string;
  canFetchMore?: boolean;
  onFetchMore?: () => void;
  areFiltersApplied: boolean;
  isSearchApplied: boolean;
  areFiltersLoading: boolean;
  areStandardFiltersApplied?: boolean;
  canSelectRows?: boolean;
  canExport?: (canExport: boolean) => void;
  selectedRowsIds?: string[];
  /**
   * Must accept an updater so `handleSelectRow` can stay referentially stable
   * (it is passed to every memoized row) instead of closing over
   * `selectedRowsIds`.
   */
  setSelectedRowsIds?: Dispatch<SetStateAction<string[]>>;
  lastPatchedRequestId?: string | null;
}

// Shared so the default does not allocate a new array per render, which would
// re-fire the selection effects and rebuild the body's lookup Set every time.
const NO_SELECTED_ROWS: string[] = [];

const RequestsTable = ({
  requests,
  lastPatchedRequestId,
  status,
  canFetchMore = false,
  onFetchMore = () => {},
  areFiltersApplied,
  isSearchApplied,
  areFiltersLoading,
  areStandardFiltersApplied,
  canSelectRows = false,
  canExport,
  selectedRowsIds = NO_SELECTED_ROWS,
  setSelectedRowsIds,
}: RequestsTableProps) => {
  const dispatch = useDispatch();
  const [rowHeaderCheckboxMode, setRowHeaderCheckboxMode] = useState<
    boolean | 'mixed'
  >(false);
  const [scrollPos, setScrollPos] = useState({ top: 0, left: 0 });

  const sortParameter = useSelector(requestsSelectors.selectSortParameter);
  const isFetchingNext = useSelector(requestsSelectors.selectIsFetchingNext);

  const containerRef = useRef<HTMLDivElement>(null);

  const updateScrollPos = () => {
    const container = containerRef.current;
    if (container) {
      setScrollPos({ top: container.scrollTop, left: container.scrollLeft });
    }
  };

  useLayoutEffect(() => {
    updateScrollPos();
  }, [sortParameter]);

  useEffect(() => {
    if (isFetchingNext) {
      updateScrollPos();
    }
  }, [isFetchingNext]);

  useEffect(() => {
    if (lastPatchedRequestId) {
      dispatch(clearLastPatchedRequestId());
      return;
    }
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: scrollPos.top, left: scrollPos.left });
    }
  }, [requests, scrollPos, lastPatchedRequestId, dispatch]);

  useEffect(() => {
    if (canExport) {
      canExport(selectedRowsIds!.length > 0);
    }
  }, [selectedRowsIds, canExport]);

  useEffect(() => {
    if (selectedRowsIds?.length === requests.length && requests.length > 0) {
      setRowHeaderCheckboxMode(true);
    } else if (selectedRowsIds?.length === 0) {
      setRowHeaderCheckboxMode(false);
    } else {
      setRowHeaderCheckboxMode('mixed');
    }
  }, [selectedRowsIds, requests]);

  // Passed to every row, so it must keep a stable identity for React.memo to
  // hold — hence the updater form rather than closing over `selectedRowsIds`.
  const handleSelectRow = useCallback(
    (rowId: string) => {
      setSelectedRowsIds!((previous) =>
        previous.includes(rowId)
          ? previous.filter((id) => id !== rowId)
          : [...previous, rowId]
      );
      setRowHeaderCheckboxMode('mixed');
      dispatch(setSelectAll(false));
    },
    [setSelectedRowsIds, dispatch]
  );

  if (
    status === SLICE_STATUS.LOADING ||
    status === SLICE_STATUS.IDLE ||
    areFiltersLoading
  ) {
    return <SkeletonLoader />;
  }

  const tableData = {
    headData: {
      headCells: [],
    },
    rows: [],
  };

  const className =
    'overflow-auto border-b border-neutral-lighter-two rounded-b';

  const handleSelectRowHeader = (checked: boolean | 'mixed') => {
    if (rowHeaderCheckboxMode === 'mixed') {
      setSelectedRowsIds!([]);
      dispatch(setSelectAll(false));
      return;
    }
    const isChecked = checked === true;
    dispatch(setSelectAll(isChecked));
    if (isChecked) {
      setSelectedRowsIds!(requests.map((request) => request.id));
    } else {
      setSelectedRowsIds!([]);
    }
  };

  return (
    <div
      className={tailwindOverride(className, {
        'max-h-table': !areStandardFiltersApplied,
        'max-h-screen-minus-16rem': areStandardFiltersApplied,
      })}
      ref={containerRef}
    >
      <Table
        className='w-full'
        data={tableData}
        canSelectRows={canSelectRows}
        selectedRowsIds={selectedRowsIds}
        onSelectRows={setSelectedRowsIds}
      >
        <RequestsTableHeader
          handleSelectRowHeader={handleSelectRowHeader}
          rowHeaderCheckboxMode={rowHeaderCheckboxMode}
        />
        <RequestsTableBody
          requests={requests}
          canFetchMore={canFetchMore}
          fetchMore={onFetchMore}
          areFiltersApplied={areFiltersApplied}
          isSearchApplied={isSearchApplied}
          canSelectRows={canSelectRows}
          onSelectRow={handleSelectRow}
          selectedRowsIds={selectedRowsIds}
        />
      </Table>
    </div>
  );
};

export default RequestsTable;
